/**
 * train-ui.tsx — Mảnh giao diện dùng chung của màn cấu hình nhân viên AI:
 * thẻ gập/mở, dòng cài đặt có công tắc, nhóm chip chọn một.
 */
import { useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Switch } from '@/components/ui/misc'
import { cn } from '@/lib/utils'

/** Thẻ gập/mở. Trạng thái mở nhớ theo `storageKey` (nếu có). */
export function CollapsibleCard({ id, title, description, icon, defaultOpen = true, actions, children, storageKey }: {
  id?: string
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  defaultOpen?: boolean
  actions?: ReactNode
  children: ReactNode
  storageKey?: string
}) {
  const [open, setOpen] = useState<boolean>(() => {
    if (!storageKey) return defaultOpen
    try {
      const v = localStorage.getItem(storageKey)
      return v === null ? defaultOpen : v === '1'
    } catch { return defaultOpen }
  })
  const toggle = () => {
    setOpen((o) => {
      if (storageKey) { try { localStorage.setItem(storageKey, o ? '0' : '1') } catch { /* bỏ qua */ } }
      return !o
    })
  }
  return (
    <section id={id} className="scroll-mt-4 rounded-xl border bg-background">
      <div className="flex flex-wrap items-start gap-2 px-4 py-3">
        <button type="button" onClick={toggle} className="flex min-w-[220px] flex-1 items-start gap-2 text-left" aria-expanded={open}>
          {icon && <span className="mt-0.5 shrink-0 text-primary">{icon}</span>}
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">{title}</span>
            {description && <span className="mt-0.5 block text-xs text-muted-foreground">{description}</span>}
          </span>
          <ChevronDown className={cn('mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
        {actions && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
      </div>
      {open && <div className="space-y-4 border-t px-4 py-4">{children}</div>}
    </section>
  )
}

/** Một dòng cài đặt: tiêu đề + mô tả bên trái, công tắc bên phải, nội dung phụ bên dưới khi bật. */
export function SettingRow({ title, description, checked, onChange, disabled, children }: {
  title: ReactNode
  description?: ReactNode
  checked?: boolean
  onChange?: (v: boolean) => void
  disabled?: boolean
  children?: ReactNode
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{title}</p>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {onChange && <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} />}
      </div>
      {children}
    </div>
  )
}

/** Nhóm chip chọn một giá trị. */
export function ChipGroup<T extends string | number>({ value, options, onChange, disabled }: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (v: T) => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-50',
            value === o.value ? 'border-primary bg-primary/10 font-medium text-primary' : 'hover:bg-muted',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Ô nhập danh sách từ (Enter / dấu phẩy để thêm). */
export function TagInput({ value, onChange, placeholder, disabled, max = 30 }: {
  value: string[]
  onChange: (v: string[]) => void
  placeholder?: string
  disabled?: boolean
  max?: number
}) {
  const { t } = useTranslation('aiTrainUi')
  const [draft, setDraft] = useState('')
  const add = (raw: string) => {
    const parts = raw.split(',').map((s) => s.trim()).filter(Boolean)
    if (!parts.length) return
    onChange([...new Set([...value, ...parts])].slice(0, max))
    setDraft('')
  }
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border px-2 py-1.5">
      {value.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs">
          {tag}
          {!disabled && (
            <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => onChange(value.filter((x) => x !== tag))} aria-label={t('trainUi.removeTag', { tag })}>×</button>
          )}
        </span>
      ))}
      <input
        className="min-w-[120px] flex-1 bg-transparent text-sm outline-none"
        value={draft}
        disabled={disabled || value.length >= max}
        placeholder={value.length >= max ? t('trainUi.tagMax', { max }) : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft) }
          else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1))
        }}
        onBlur={() => add(draft)}
      />
    </div>
  )
}
